/**
 * Registre des product tours, publié pour la console (spec V1 : `tour-registry.json` au build).
 * - Un **contexte** est un écran dans une variante donnée (`declaration.beta`) : une route et un
 *   marqueur `data-tour-context` posé sur la racine de l'écran. Un tour vise un contexte : il n'est
 *   proposé que si l'URL correspond et que le marqueur est à l'écran. C'est ce qui sépare la beta de
 *   l'interface classique, qui partagent la même route.
 * - Une **ancre** est un élément `data-tour` qu'une étape peut mettre en avant, rattaché aux
 *   contextes où il existe. La console ne propose que les ancres du contexte du tour.
 * - Une **action** ouvre quelque chose avant une étape (un onglet, un panneau) et le referme après.
 *   L'écran la déclare avec `useTourActions` ; `reveals` liste les ancres qu'elle rend visibles.
 * Un id ne change jamais : en créer un nouveau, retirer l'ancien quand plus aucun tour publié ne
 * l'utilise.
 */
export type TourContext = {
  key: string
  /** Motif react-router de l'écran. */
  route: string
  /** Libellé lu par le CSM dans la console. */
  label: string
  description?: string
}

export type TourAnchor = {
  id: string
  /** Contextes où l'ancre existe ; `all` pour un élément commun (sidenav). */
  contexts: readonly string[] | 'all'
  /** Libellé lu par le CSM dans la console. */
  label: string
  description?: string
}

export type TourAction = {
  id: string
  contexts: readonly string[]
  /** Libellé lu par le CSM dans la console. */
  label: string
  description?: string
  /** Ancres rendues visibles par l'action : la console la propose quand on en choisit une. */
  reveals?: readonly string[]
  /** Bouton ou onglet qui fait la même chose : le tour le montre avant d'ouvrir, pour faire le lien. */
  trigger?: string
}

export const TOUR_CONTEXTS = {
  declarationBeta: 'declaration.beta',
} as const

export const TOUR_CONTEXT_REGISTRY: readonly TourContext[] = [
  {
    key: TOUR_CONTEXTS.declarationBeta,
    route: '/documents/:id/declaration',
    label: 'Déclaration · interface beta',
    description: 'Écran dossier de /declaration avec le switch beta activé.',
  },
]

export const TOUR_ANCHORS = {
  sidenavTourButton: 'sidenav.tour-button',
  declarationStepsNav: 'declaration.steps.nav',
  declarationFieldsSearch: 'declaration.fields.search',
  declarationFieldsExpandAll: 'declaration.fields.expand-all',
  declarationAssistantButton: 'declaration.actions.assistant',
  declarationDauRecapButton: 'declaration.actions.dau-recap',
  declarationSourceDocumentButton: 'declaration.actions.source-document',
  declarationInterfaceToggle: 'declaration.header.interface-toggle',
  declarationControlButton: 'declaration.header.control',
  declarationStartReviewButton: 'declaration.control.start-review',
  declarationHistoryButton: 'declaration.header.history',
  declarationUndoButton: 'declaration.header.undo',
  declarationRedoButton: 'declaration.header.redo',
  declarationSettingsButton: 'declaration.header.settings',
  declarationShortcutsButton: 'declaration.header.shortcuts',
  declarationValidateButton: 'declaration.header.validate',
  declarationStepHeaders: 'declaration.steps.headers',
  declarationStepArticles: 'declaration.steps.articles',
  declarationStepDelivery: 'declaration.steps.delivery',
  declarationStepRegulation: 'declaration.steps.regulation',
  declarationStepControl: 'declaration.steps.control',
  declarationFirstSection: 'declaration.fields.first-section',
  declarationFirstField: 'declaration.fields.first-field',
  declarationArticlesMacroCheck: 'declaration.articles.macro-check',
  declarationArticlesTable: 'declaration.articles.table',
  declarationArticlesSearch: 'declaration.articles.search',
  declarationArticlesColumns: 'declaration.articles.columns',
  declarationArticlesAddColumn: 'declaration.articles.add-column',
  declarationArticlesConsolidate: 'declaration.articles.consolidate',
  declarationArticlesAddArticle: 'declaration.articles.add-article',
  declarationControlStepSummary: 'declaration.control-step.summary',
  declarationControlPanel: 'declaration.control.panel',
  declarationControlAnomalies: 'declaration.control.anomalies',
  declarationAssistantWindow: 'declaration.assistant.window',
  declarationAssistantInput: 'declaration.assistant.input',
  declarationSourceDocumentWindow: 'declaration.source-document.window',
  declarationDauRecapOverlay: 'declaration.dau-recap.overlay',
  declarationShortcutsPanel: 'declaration.shortcuts.panel',
  declarationControlPanelPin: 'declaration.control.pin',
  declarationControlPanelClose: 'declaration.control.close',
  declarationAssistantPin: 'declaration.assistant.pin',
  declarationSourceDocumentPin: 'declaration.source-document.pin',
  declarationSourceDocumentFullscreen: 'declaration.source-document.fullscreen',
  declarationSourceDocumentOtherWindow:
    'declaration.source-document.other-window',
  declarationSourceDocumentClose: 'declaration.source-document.close',
  declarationArticlesToggleColumns: 'declaration.articles.toggle-columns',
  declarationArticlesUnconsolidate: 'declaration.articles.unconsolidate',
  declarationArticlesShowSourceLines: 'declaration.articles.show-source-lines',
  declarationArticlesShowConsolidated: 'declaration.articles.show-consolidated',
  declarationArticlesExpandMerged: 'declaration.articles.expand-merged',
  declarationArticlesPinColumn: 'declaration.articles.pin-column',
  declarationArticlesSetColumn: 'declaration.articles.set-column',
  declarationArticlesClearColumn: 'declaration.articles.clear-column',
  declarationArticlesRemoveColumn: 'declaration.articles.remove-column',
  declarationArticlesDeleteArticle: 'declaration.articles.delete-article',
  // Tarifill (TEC-1807), derrière le flag `tarifill` : la fenêtre et son tour local.
  tarifillWindowHeader: 'tarifill.window.header',
  tarifillWindowHelp: 'tarifill.window.help',
  tarifillWindowDirectory: 'tarifill.window.directory',
  tarifillListSummary: 'tarifill.list.summary',
  tarifillListFilters: 'tarifill.list.filters',
  tarifillListReview: 'tarifill.list.review',
  tarifillListCompletion: 'tarifill.list.completion',
  tarifillListCompletionButton: 'tarifill.list.completion-button',
  tarifillPreviewTitle: 'tarifill.preview.title',
  tarifillPreviewTable: 'tarifill.preview.table',
  tarifillPreviewCheckbox: 'tarifill.preview.checkbox',
  tarifillPreviewCompleted: 'tarifill.preview.completed',
  tarifillPreviewLabel: 'tarifill.preview.label',
  tarifillPreviewSkipped: 'tarifill.preview.skipped',
  tarifillPreviewAuto: 'tarifill.preview.auto',
  tarifillPreviewConfirm: 'tarifill.preview.confirm',
  tarifillCard: 'tarifill.card.root',
  tarifillCardPath: 'tarifill.card.path',
  tarifillCardOptions: 'tarifill.card.options',
  tarifillCardApply: 'tarifill.card.apply',
  tarifillCardCost: 'tarifill.card.cost',
  tarifillDetailBrowse: 'tarifill.detail.browse',
  tarifillDetailCode: 'tarifill.detail.code',
  tarifillDetailPath: 'tarifill.detail.path',
  tarifillDirectoryTarget: 'tarifill.directory.target',
  tarifillDirectoryScope: 'tarifill.directory.scope',
  tarifillDirectorySearch: 'tarifill.directory.search',
  tarifillDirectoryResults: 'tarifill.directory.results',
  tarifillDirectoryTree: 'tarifill.directory.tree',
  tarifillDirectoryApply: 'tarifill.directory.apply',
  tarifillGridCode: 'tarifill.grid.code',
} as const

export type TourAnchorId = (typeof TOUR_ANCHORS)[keyof typeof TOUR_ANCHORS]

const BETA = [TOUR_CONTEXTS.declarationBeta]

export const TOUR_ANCHOR_REGISTRY: readonly TourAnchor[] = [
  {
    id: TOUR_ANCHORS.sidenavTourButton,
    contexts: 'all',
    label: 'Sidenav · visite guidée',
    description:
      'Relance le tour de l’écran affiché. Visible seulement quand un tour y est disponible.',
  },
  {
    id: TOUR_ANCHORS.declarationStepsNav,
    contexts: BETA,
    label: 'Étapes',
    description: 'Onglets des étapes, en haut de la page.',
  },
  {
    id: TOUR_ANCHORS.declarationFieldsSearch,
    contexts: BETA,
    label: 'Recherche de champs',
    description: 'Visible sur les étapes de formulaire.',
  },
  {
    id: TOUR_ANCHORS.declarationFieldsExpandAll,
    contexts: BETA,
    label: 'Tout déplier',
    description:
      'Déplie ou replie les sections. Visible sur les étapes de formulaire.',
  },
  {
    id: TOUR_ANCHORS.declarationAssistantButton,
    contexts: BETA,
    label: 'Bouton Assistant',
  },
  {
    id: TOUR_ANCHORS.declarationDauRecapButton,
    contexts: BETA,
    label: 'Bouton récap DAU',
  },
  {
    id: TOUR_ANCHORS.declarationSourceDocumentButton,
    contexts: BETA,
    label: 'Bouton document source',
  },
  {
    id: TOUR_ANCHORS.declarationControlButton,
    contexts: BETA,
    label: 'Bouton Vérifications',
    description:
      'Ouvre le panneau Vérifications ; sa pastille compte ce qui reste à traiter. Remplace la jauge de l’ancien prototype.',
  },
  {
    id: TOUR_ANCHORS.declarationStartReviewButton,
    contexts: BETA,
    label: 'Bouton Passer en revue',
    description:
      'Le ▶ collé au bouton Vérifications, dans le bandeau. Grisé s’il ne reste rien à passer en revue.',
  },
  {
    id: TOUR_ANCHORS.declarationInterfaceToggle,
    contexts: BETA,
    label: 'Switch beta',
    description: 'Bascule entre interface classique et beta.',
  },
  ...(
    [
      [
        TOUR_ANCHORS.declarationHistoryButton,
        'Bouton Historique',
        'Journal du dossier et des corrections, avec leur annulation.',
      ],
      [
        TOUR_ANCHORS.declarationUndoButton,
        'Bouton Annuler',
        'Annule la dernière correction (⌘Z).',
      ],
      [
        TOUR_ANCHORS.declarationRedoButton,
        'Bouton Rétablir',
        'Rétablit la correction annulée (⌘⇧Z).',
      ],
      [
        TOUR_ANCHORS.declarationSettingsButton,
        'Bouton Paramètres',
        'Paramètres et commentaires du dossier.',
      ],
      [
        TOUR_ANCHORS.declarationShortcutsButton,
        'Bouton Raccourcis clavier',
        'Affiche les raccourcis (⌘I).',
      ],
      [
        TOUR_ANCHORS.declarationValidateButton,
        'Bouton Valider',
        'Valider, ou Valider et envoyer.',
      ],
      [TOUR_ANCHORS.declarationStepHeaders, 'Onglet Entêtes', undefined],
      [TOUR_ANCHORS.declarationStepArticles, 'Onglet Articles', undefined],
      [
        TOUR_ANCHORS.declarationStepDelivery,
        'Onglet Livraison & Transport',
        undefined,
      ],
      [
        TOUR_ANCHORS.declarationStepRegulation,
        'Onglet Réglementation & Douane',
        undefined,
      ],
      [TOUR_ANCHORS.declarationStepControl, 'Onglet Totaux', undefined],
      [
        TOUR_ANCHORS.declarationFirstSection,
        'Première rubrique',
        'Première rubrique de l’onglet affiché (onglets de formulaire).',
      ],
      [
        TOUR_ANCHORS.declarationFirstField,
        'Premier champ',
        'Premier champ de la première rubrique, si elle est dépliée.',
      ],
      [
        TOUR_ANCHORS.declarationArticlesMacroCheck,
        'Contrôles des articles',
        'Montant, colis et masses comparés au document (onglet Articles).',
      ],
      [
        TOUR_ANCHORS.declarationArticlesTable,
        'Tableau des articles',
        'Onglet Articles.',
      ],
      [
        TOUR_ANCHORS.declarationArticlesSearch,
        'Recherche d’article',
        'Onglet Articles.',
      ],
      [
        TOUR_ANCHORS.declarationArticlesColumns,
        'Colonnes du tableau',
        'Toutes les colonnes ou seulement les renseignées (onglet Articles, s’il y a des colonnes masquées).',
      ],
      [
        TOUR_ANCHORS.declarationArticlesAddColumn,
        'Ajouter une colonne',
        'Onglet Articles.',
      ],
      [
        TOUR_ANCHORS.declarationArticlesConsolidate,
        'Consolider les articles',
        'Onglet Articles, seulement si une consolidation est possible.',
      ],
      [
        TOUR_ANCHORS.declarationArticlesAddArticle,
        'Ajouter un article',
        'Onglet Articles, déclaration modifiable.',
      ],
      [
        TOUR_ANCHORS.declarationControlStepSummary,
        'Synthèse Totaux',
        'Cartes et totaux de l’onglet Totaux.',
      ],
      [
        TOUR_ANCHORS.declarationControlPanel,
        'Panneau Vérifications',
        'Visible une fois le panneau ouvert.',
      ],
      [
        TOUR_ANCHORS.declarationControlAnomalies,
        'Liste des anomalies',
        'Dans le panneau Vérifications.',
      ],
      [
        TOUR_ANCHORS.declarationAssistantWindow,
        'Fenêtre de l’assistant',
        'Visible une fois l’assistant ouvert.',
      ],
      [
        TOUR_ANCHORS.declarationAssistantInput,
        'Saisie de l’assistant',
        'Zone où l’on écrit à l’assistant.',
      ],
      [
        TOUR_ANCHORS.declarationSourceDocumentWindow,
        'Fenêtre du document source',
        'Visible une fois le document ouvert.',
      ],
      [
        TOUR_ANCHORS.declarationDauRecapOverlay,
        'Récap DAU ouvert',
        'Le formulaire DAU en plein écran.',
      ],
      [
        TOUR_ANCHORS.declarationShortcutsPanel,
        'Panneau des raccourcis',
        'Visible une fois les raccourcis affichés.',
      ],
      [
        TOUR_ANCHORS.declarationControlPanelPin,
        'Épingler le panneau Vérifications',
        'Le fixer à gauche ou à droite de la page.',
      ],
      [
        TOUR_ANCHORS.declarationControlPanelClose,
        'Fermer le panneau Vérifications',
        undefined,
      ],
      [
        TOUR_ANCHORS.declarationAssistantPin,
        'Épingler l’assistant',
        'Le fixer à gauche ou à droite de la page.',
      ],
      [
        TOUR_ANCHORS.declarationSourceDocumentPin,
        'Épingler le document source',
        'Le fixer à gauche ou à droite de la page.',
      ],
      [
        TOUR_ANCHORS.declarationSourceDocumentFullscreen,
        'Document source en plein écran',
        undefined,
      ],
      [
        TOUR_ANCHORS.declarationSourceDocumentOtherWindow,
        'Ouvrir le document dans une autre fenêtre',
        'Pour un second écran (⌘⇧E).',
      ],
      [
        TOUR_ANCHORS.declarationSourceDocumentClose,
        'Fermer le document source',
        undefined,
      ],
      [
        TOUR_ANCHORS.declarationArticlesToggleColumns,
        'Toutes les colonnes / colonnes renseignées',
        'Onglet Articles, s’il y a des colonnes masquées.',
      ],
      [
        TOUR_ANCHORS.declarationArticlesUnconsolidate,
        'Annuler la consolidation',
        'Onglet Articles, articles consolidés.',
      ],
      [
        TOUR_ANCHORS.declarationArticlesShowSourceLines,
        'Afficher les lignes d’origine',
        'Onglet Articles, articles consolidés.',
      ],
      [
        TOUR_ANCHORS.declarationArticlesShowConsolidated,
        'Afficher les articles consolidés',
        'Onglet Articles, articles consolidés.',
      ],
      [
        TOUR_ANCHORS.declarationArticlesExpandMerged,
        'Déplier les lignes d’origine',
        'Onglet Articles, articles fusionnés.',
      ],
      [
        TOUR_ANCHORS.declarationArticlesPinColumn,
        'Figer une colonne',
        'En-tête de la première colonne du tableau.',
      ],
      [
        TOUR_ANCHORS.declarationArticlesSetColumn,
        'Appliquer une valeur à toute la colonne',
        'En-tête de la première colonne qui le permet.',
      ],
      [
        TOUR_ANCHORS.declarationArticlesClearColumn,
        'Vider une colonne',
        'En-tête de la première colonne qui le permet.',
      ],
      [
        TOUR_ANCHORS.declarationArticlesRemoveColumn,
        'Retirer une colonne',
        'En-tête de la première colonne retirable.',
      ],
      [
        TOUR_ANCHORS.declarationArticlesDeleteArticle,
        'Supprimer un article',
        'Première ligne du tableau.',
      ],
    ] as const
  ).map(([id, label, description]) => ({
    id,
    contexts: BETA,
    label,
    ...(description && { description }),
  })),
  {
    id: TOUR_ANCHORS.tarifillWindowHeader,
    contexts: BETA,
    label: 'Tarifill · en-tête de la fenêtre',
  },
  {
    id: TOUR_ANCHORS.tarifillWindowHelp,
    contexts: BETA,
    label: 'Tarifill · bouton d’aide (tour)',
  },
  {
    id: TOUR_ANCHORS.tarifillWindowDirectory,
    contexts: BETA,
    label: 'Tarifill · bouton Annuaire',
  },
  {
    id: TOUR_ANCHORS.tarifillListSummary,
    contexts: BETA,
    label: 'Tarifill · synthèse des articles prêts',
  },
  {
    id: TOUR_ANCHORS.tarifillListFilters,
    contexts: BETA,
    label: 'Tarifill · filtres de la liste',
  },
  {
    id: TOUR_ANCHORS.tarifillListReview,
    contexts: BETA,
    label: 'Tarifill · bouton « Traiter en série »',
  },
  {
    id: TOUR_ANCHORS.tarifillListCompletion,
    contexts: BETA,
    label: 'Tarifill · encart de complétion vers « autres »',
  },
  {
    id: TOUR_ANCHORS.tarifillListCompletionButton,
    contexts: BETA,
    label: 'Tarifill · bouton « Vérifier et compléter »',
  },
  {
    id: TOUR_ANCHORS.tarifillPreviewTitle,
    contexts: BETA,
    label: 'Tarifill · aperçu, titre',
  },
  {
    id: TOUR_ANCHORS.tarifillPreviewTable,
    contexts: BETA,
    label: 'Tarifill · aperçu, tableau',
  },
  {
    id: TOUR_ANCHORS.tarifillPreviewCheckbox,
    contexts: BETA,
    label: 'Tarifill · aperçu, case à cocher',
  },
  {
    id: TOUR_ANCHORS.tarifillPreviewCompleted,
    contexts: BETA,
    label: 'Tarifill · aperçu, code complété',
  },
  {
    id: TOUR_ANCHORS.tarifillPreviewLabel,
    contexts: BETA,
    label: 'Tarifill · aperçu, libellé',
  },
  {
    id: TOUR_ANCHORS.tarifillPreviewSkipped,
    contexts: BETA,
    label: 'Tarifill · aperçu, codes laissés',
  },
  {
    id: TOUR_ANCHORS.tarifillPreviewAuto,
    contexts: BETA,
    label: 'Tarifill · aperçu, pastille Auto',
  },
  {
    id: TOUR_ANCHORS.tarifillPreviewConfirm,
    contexts: BETA,
    label: 'Tarifill · aperçu, confirmation',
  },
  {
    id: TOUR_ANCHORS.tarifillCard,
    contexts: BETA,
    label: 'Tarifill · carte article',
  },
  {
    id: TOUR_ANCHORS.tarifillCardPath,
    contexts: BETA,
    label: 'Tarifill · carte, fil des niveaux',
  },
  {
    id: TOUR_ANCHORS.tarifillCardOptions,
    contexts: BETA,
    label: 'Tarifill · carte, suites possibles',
  },
  {
    id: TOUR_ANCHORS.tarifillCardApply,
    contexts: BETA,
    label: 'Tarifill · carte, bouton Appliquer',
  },
  {
    id: TOUR_ANCHORS.tarifillCardCost,
    contexts: BETA,
    label: 'Tarifill · carte, coût estimé',
  },
  {
    id: TOUR_ANCHORS.tarifillDetailBrowse,
    contexts: BETA,
    label: 'Tarifill · fiche, parcours des articles',
  },
  {
    id: TOUR_ANCHORS.tarifillDetailCode,
    contexts: BETA,
    label: 'Tarifill · fiche, code de l’article',
  },
  {
    id: TOUR_ANCHORS.tarifillDetailPath,
    contexts: BETA,
    label: 'Tarifill · fiche, chemin dans la nomenclature',
  },
  {
    id: TOUR_ANCHORS.tarifillDirectoryTarget,
    contexts: BETA,
    label: 'Tarifill · annuaire, article ciblé',
  },
  {
    id: TOUR_ANCHORS.tarifillDirectoryScope,
    contexts: BETA,
    label: 'Tarifill · annuaire, portée',
  },
  {
    id: TOUR_ANCHORS.tarifillDirectorySearch,
    contexts: BETA,
    label: 'Tarifill · annuaire, recherche',
  },
  {
    id: TOUR_ANCHORS.tarifillDirectoryResults,
    contexts: BETA,
    label: 'Tarifill · annuaire, résultats',
  },
  {
    id: TOUR_ANCHORS.tarifillDirectoryTree,
    contexts: BETA,
    label: 'Tarifill · annuaire, arborescence',
  },
  {
    id: TOUR_ANCHORS.tarifillDirectoryApply,
    contexts: BETA,
    label: 'Tarifill · annuaire, appliquer le code',
  },
  {
    id: TOUR_ANCHORS.tarifillGridCode,
    contexts: BETA,
    label: 'Tarifill · grille, icône de la case code',
  },
]

/** `<page>.<zone>.<élément>` en kebab-case : rien à échapper dans le sélecteur. */
export const TOUR_ANCHOR_ID_PATTERN = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/

export const tourAnchorSelector = (anchor: string): string =>
  `[data-tour="${anchor}"]`

export const tourContextSelector = (context: string): string =>
  `[data-tour-context="${context}"]`

export const tourContextRoute = (context: string): string | undefined =>
  TOUR_CONTEXT_REGISTRY.find(({ key }) => key === context)?.route

export const TOUR_ACTIONS = {
  declarationGotoHeaders: 'declaration.goto.headers',
  declarationGotoArticles: 'declaration.goto.articles',
  declarationGotoDelivery: 'declaration.goto.delivery',
  declarationGotoRegulation: 'declaration.goto.regulation',
  declarationGotoControl: 'declaration.goto.control',
  declarationOpenControlPanel: 'declaration.open.control-panel',
  declarationOpenAssistant: 'declaration.open.assistant',
  declarationOpenSourceDocument: 'declaration.open.source-document',
  declarationOpenDauRecap: 'declaration.open.dau-recap',
  declarationOpenShortcuts: 'declaration.open.shortcuts',
} as const

export type TourActionId = (typeof TOUR_ACTIONS)[keyof typeof TOUR_ACTIONS]

const FORM_STEP_ANCHORS = [
  TOUR_ANCHORS.declarationFieldsSearch,
  TOUR_ANCHORS.declarationFieldsExpandAll,
  TOUR_ANCHORS.declarationFirstSection,
  TOUR_ANCHORS.declarationFirstField,
]

export const TOUR_ACTION_REGISTRY: readonly TourAction[] = [
  {
    id: TOUR_ACTIONS.declarationGotoHeaders,
    contexts: BETA,
    trigger: TOUR_ANCHORS.declarationStepHeaders,
    label: 'Aller à l’onglet Entêtes',
    reveals: FORM_STEP_ANCHORS,
  },
  {
    id: TOUR_ACTIONS.declarationGotoArticles,
    contexts: BETA,
    trigger: TOUR_ANCHORS.declarationStepArticles,
    label: 'Aller à l’onglet Articles',
    reveals: [
      TOUR_ANCHORS.declarationArticlesMacroCheck,
      TOUR_ANCHORS.declarationArticlesTable,
      TOUR_ANCHORS.declarationArticlesSearch,
      TOUR_ANCHORS.declarationArticlesColumns,
      TOUR_ANCHORS.declarationArticlesAddColumn,
      TOUR_ANCHORS.declarationArticlesConsolidate,
      TOUR_ANCHORS.declarationArticlesAddArticle,
      TOUR_ANCHORS.declarationArticlesToggleColumns,
      TOUR_ANCHORS.declarationArticlesUnconsolidate,
      TOUR_ANCHORS.declarationArticlesShowSourceLines,
      TOUR_ANCHORS.declarationArticlesShowConsolidated,
      TOUR_ANCHORS.declarationArticlesExpandMerged,
      TOUR_ANCHORS.declarationArticlesPinColumn,
      TOUR_ANCHORS.declarationArticlesSetColumn,
      TOUR_ANCHORS.declarationArticlesClearColumn,
      TOUR_ANCHORS.declarationArticlesRemoveColumn,
      TOUR_ANCHORS.declarationArticlesDeleteArticle,
    ],
  },
  {
    id: TOUR_ACTIONS.declarationGotoDelivery,
    contexts: BETA,
    trigger: TOUR_ANCHORS.declarationStepDelivery,
    label: 'Aller à l’onglet Livraison & Transport',
    reveals: FORM_STEP_ANCHORS,
  },
  {
    id: TOUR_ACTIONS.declarationGotoRegulation,
    contexts: BETA,
    trigger: TOUR_ANCHORS.declarationStepRegulation,
    label: 'Aller à l’onglet Réglementation & Douane',
    reveals: FORM_STEP_ANCHORS,
  },
  {
    id: TOUR_ACTIONS.declarationGotoControl,
    contexts: BETA,
    trigger: TOUR_ANCHORS.declarationStepControl,
    label: 'Aller à l’onglet Totaux',
    reveals: [TOUR_ANCHORS.declarationControlStepSummary],
  },
  {
    id: TOUR_ACTIONS.declarationOpenControlPanel,
    contexts: BETA,
    trigger: TOUR_ANCHORS.declarationControlButton,
    label: 'Ouvrir le panneau Vérifications',
    reveals: [
      TOUR_ANCHORS.declarationControlPanel,
      TOUR_ANCHORS.declarationControlAnomalies,
      TOUR_ANCHORS.declarationControlPanelPin,
      TOUR_ANCHORS.declarationControlPanelClose,
    ],
  },
  {
    id: TOUR_ACTIONS.declarationOpenAssistant,
    contexts: BETA,
    trigger: TOUR_ANCHORS.declarationAssistantButton,
    label: 'Ouvrir l’assistant',
    reveals: [
      TOUR_ANCHORS.declarationAssistantWindow,
      TOUR_ANCHORS.declarationAssistantInput,
      TOUR_ANCHORS.declarationAssistantPin,
    ],
  },
  {
    id: TOUR_ACTIONS.declarationOpenSourceDocument,
    contexts: BETA,
    trigger: TOUR_ANCHORS.declarationSourceDocumentButton,
    label: 'Ouvrir le document source',
    reveals: [
      TOUR_ANCHORS.declarationSourceDocumentWindow,
      TOUR_ANCHORS.declarationSourceDocumentPin,
      TOUR_ANCHORS.declarationSourceDocumentFullscreen,
      TOUR_ANCHORS.declarationSourceDocumentOtherWindow,
      TOUR_ANCHORS.declarationSourceDocumentClose,
    ],
  },
  {
    id: TOUR_ACTIONS.declarationOpenDauRecap,
    contexts: BETA,
    trigger: TOUR_ANCHORS.declarationDauRecapButton,
    label: 'Ouvrir le récap DAU',
    description: 'Seulement si la déclaration a des articles.',
    reveals: [TOUR_ANCHORS.declarationDauRecapOverlay],
  },
  {
    id: TOUR_ACTIONS.declarationOpenShortcuts,
    contexts: BETA,
    trigger: TOUR_ANCHORS.declarationShortcutsButton,
    label: 'Afficher les raccourcis clavier',
    reveals: [TOUR_ANCHORS.declarationShortcutsPanel],
  },
]

export const tourActionTrigger = (action: string): string | undefined =>
  TOUR_ACTION_REGISTRY.find(({ id }) => id === action)?.trigger
